
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useProviderQuotes } from './hooks/useProviderQuotes';
import { QuoteRow } from './components/QuoteRow';
import { PEOQuote } from '@/types/quotes.types';
import { QuoteEditDialog } from './components/QuoteEditDialog';
import { useQuoteEdit } from './hooks/useQuoteEdit';
import { LoadingSpinner } from '@/components/auth/LoadingSpinner';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';

interface QuotesListProps {
  providerId: string;
}

export const QuotesList = ({ providerId }: QuotesListProps) => {
  const { 
    data: quotes, 
    isLoading, 
    isError, 
    refetch, 
    isFetching, 
    error,
    debugInfo 
  } = useProviderQuotes(providerId);
  
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  const {
    selectedQuote,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isSubmitting,
    uploadError,
    editForm,
    setEditForm,
    openEditDialog,
    handleEditQuote
  } = useQuoteEdit(() => {
    console.log("Quote edit successful, refreshing quotes list");
    // Refresh quotes list after successful edit with a small delay to ensure DB update is complete
    handleRefresh();
  });

  // Automatically refresh quotes when component mounts
  useEffect(() => {
    console.log("QuotesList mounted, refreshing quotes data");
    refetch();
  }, [refetch]);

  const handleEditClick = (quote: PEOQuote) => {
    console.log("Edit click for quote:", quote);
    openEditDialog(quote);
  };
  
  const handleRefresh = () => {
    setRefreshing(true);
    console.log("Manually refreshing quotes data");
    
    setTimeout(() => {
      refetch().finally(() => {
        setRefreshing(false);
        setLastRefresh(new Date());
      });
    }, 500);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Quotes Management</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Quotes Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Failed to load quotes. Please try refreshing the page.'}
            </AlertDescription>
          </Alert>
          
          <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
            <h3 className="text-sm font-semibold mb-2">Debug Information</h3>
            <pre className="text-xs whitespace-pre-wrap bg-gray-100 p-2 rounded">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={handleRefresh}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!quotes || quotes.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Quotes Management</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No quotes yet</AlertTitle>
            <AlertDescription>
              You don't have any quotes yet. When an administrator creates quotes for your PEO services, they will appear here.
            </AlertDescription>
          </Alert>
          
          <div className="mt-4 bg-gray-50 p-4 rounded-md border border-gray-200">
            <h3 className="text-sm font-semibold mb-2">Debug Information</h3>
            <pre className="text-xs whitespace-pre-wrap bg-gray-100 p-2 rounded">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Debug log to check quotes data
  console.log("Rendering quotes list with data:", quotes);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-bold bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
            Quotes Management {isFetching && !refreshing && <span className="text-sm text-muted-foreground">(refreshing...)</span>}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing || isFetching}
              className="flex items-center gap-1"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing || isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Company</TableHead>
                  <TableHead>Monthly Cost</TableHead>
                  <TableHead>Annual Cost</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((quote) => (
                  <QuoteRow
                    key={quote.id} 
                    quote={quote} 
                    onEdit={handleEditClick}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <QuoteEditDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        editForm={editForm}
        setEditForm={setEditForm}
        selectedQuote={selectedQuote}
        isSubmitting={isSubmitting}
        uploadError={uploadError}
        onSave={handleEditQuote}
      />
    </>
  );
};
